export function initMangaEdit({ pageData, origSrc, inpaintSrc, exportExt, fileBaseName }) {
const PAGE = pageData;
const PAGE_KEY = Object.keys(PAGE)[0];
const DOC = PAGE[PAGE_KEY];
const PAGE_W = DOC.original_width;
const PAGE_H = DOC.original_height;
const REGIONS = DOC.regions || [];
const EXPORT_EXT = (exportExt || '.png').toLowerCase();
const EXPORT_NAME = fileBaseName || PAGE_KEY.replace(/^.*[\\\/]/, '');
const ORIG_SRC = origSrc;
const INPAINT_SRC = inpaintSrc;

/* ================================================================
 * 常量 — 逐项照抄 manga_translator/rendering/text_render.py
 * ================================================================ */
// _BR_RE = r'\s*(?:\[BR\]|<br\s*/?>|【BR】|\r\n|\r|\n)\s*'  (IGNORECASE)
const BR_RE_SRC = '\\s*(?:\\[BR\\]|<br\\s*/?>|【BR】|\\r\\n|\\r|\\n)\\s*';
const brRe = () => new RegExp(BR_RE_SRC, 'gi');
// _H_BLOCK_RE = r'(<H>.*?</H>)' (IGNORECASE | DOTALL)
const H_BLOCK_SPLIT_RE = () => /(<H>.*?<\/H>)/gis;
const H_BLOCK_MATCH_RE = () => /<H>(.*?)<\/H>/gis;

// _HORIZONTAL_SYMBOL_HALFWIDTH_MAP = {'！':'!', '？':'?'}
const H_SYM_HALF = {'！':'!', '？':'?'};

// _VERTICAL_OPEN_BRACKETS / _VERTICAL_CLOSE_BRACKETS
const V_OPEN  = new Set(['「','『','（','《','〈','【','〔','［','｛','(','“','‘','﹁','﹃','︵','︷','︹','︻','︽','︿','﹇']);
const V_CLOSE = new Set(['」','』','）','》','〉','】','〕','］','｝',')','”','’','﹂','﹄','︶','︸','︺','︼','︾','﹀','﹈']);
// _VERTICAL_HALF_ADVANCE = OPEN | CLOSE
const V_HALF = new Set([...V_OPEN, ...V_CLOSE]);
// _VERTICAL_ALIGN_*
const V_TOP_RIGHT     = new Set(['﹁','﹃']);
const V_BOTTOM_LEFT   = new Set(['﹂','﹄']);
const V_TOP_CENTER    = new Set(['︵','︷','︹','︻','︽','︿','﹇']);
const V_BOTTOM_CENTER = new Set(['︶','︸','︺','︼','︾','﹀','﹈']);
// _is_vertical_ellipsis_char
const V_ELLIPSIS = new Set(['︙','⋮','⋯','…']);

// LANGUAGE_ORIENTATION_PRESETS（rendering/textblock.py）
const LANG_ORIENT = {
  CHS:'auto', CHT:'auto', CSY:'h', NLD:'h', ENG:'h', FRA:'h', DEU:'h', HUN:'h',
  ITA:'h', JPN:'auto', KOR:'h', POL:'h', PTB:'h', ROM:'h', RUS:'h', ESP:'h',
  THA:'h', TRK:'h', UKR:'h', VIN:'h', ARA:'hr', FIL:'h'
};

/* ================================================================
 * 工具
 * ================================================================ */
const $ = id => document.getElementById(id);
function clamp(v,a,b){ return v<a?a:(v>b?b:v); }
// Python int(): 向零截断
const pInt = v => v<0 ? Math.ceil(v) : Math.floor(v);
function rgbStr(c){ return `rgb(${c[0]|0},${c[1]|0},${c[2]|0})`; }

// is_right_to_left_char（generic.py:168）— 字符区间照抄
function isRTLChar(ch){
  const cp = ch.codePointAt(0);
  return (cp>=0x0600&&cp<=0x06FF) || (cp>=0x0750&&cp<=0x077F) ||
         (cp>=0x08A0&&cp<=0x08FF) || (cp>=0xFB50&&cp<=0xFDFF) ||
         (cp>=0xFE70&&cp<=0xFEFF) || (cp>=0x10E60&&cp<=0x10E7F) ||
         (cp>=0x1EE00&&cp<=0x1EEFF);
}
// is_valuable_char 近似（is_punctuation/is_control/is_whitespace/digit 之外）
function isValuableChar(ch){
  if (/\s/.test(ch)) return false;
  if (/\p{Nd}/u.test(ch)) return false;
  if (/\p{P}|\p{S}|\p{C}/u.test(ch)) return false;
  return true;
}

/* ================================================================
 * TextBlock 语义（utils/textblock.py）
 * ================================================================ */
// direction property（textblock.py:518）
function directionOf(r){
  let d = String(r.direction ?? 'auto').toLowerCase();
  d = ({horizontal:'h', vertical:'v'})[d] || d;
  if (['h','v','hr','vr'].includes(d)) return d;
  const preset = LANG_ORIENT[r.target_lang];
  if (preset && preset !== 'auto') return preset;
  const lines = r.lines || [];
  if (lines.length){
    // 面积最大的检测框宽高比 <1 → 竖排
    let maxArea = 0, aspect = 1;
    for (const line of lines){
      // 鞋带公式求面积
      let a = 0;
      for (let i=0;i<line.length;i++){
        const [x1,y1] = line[i], [x2,y2] = line[(i+1)%line.length];
        a += x1*y2 - x2*y1;
      }
      a = Math.abs(a)/2;
      if (a > maxArea){
        maxArea = a;
        const xs = line.map(p=>p[0]), ys = line.map(p=>p[1]);
        const w = Math.max(...xs)-Math.min(...xs);
        const h = Math.max(...ys)-Math.min(...ys);
        aspect = h>0 ? w/h : 1;
      }
    }
    return aspect < 1 ? 'v' : 'h';
  }
  return 'h'; // aspect_ratio fallback（本数据均有 lines）
}
const isHorizontal = r => directionOf(r).startsWith('h');

// alignment property（textblock.py:576）
function alignmentOf(r){
  const a = r.alignment;
  if (a==='left'||a==='center'||a==='right') return a;
  if ((r.lines||[]).length === 1) return 'center';
  const d = directionOf(r);
  if (d==='h') return 'center';
  if (d==='hr') return 'right';
  return 'left';
}

// get_translation_for_rendering（textblock.py:442）— rtl 时反转 l2r 片段
function translationForRendering(r){
  let text = r.translation || '';
  const dir = directionOf(r);
  if (!dir.endsWith('r')) return text;
  const chars = [...text];
  let l2rIdx = -1;
  const rev = (i1,i2) => {
    const delta = i2-i1;
    for (let j1=i1; j1<i2-Math.floor(delta/2); j1++){
      const j2 = i2-(j1-i1)-1;
      [chars[j1],chars[j2]] = [chars[j2],chars[j1]];
    }
  };
  let i = 0;
  for (i=0;i<chars.length;i++){
    const c = chars[i];
    if (!isRTLChar(c) && isValuableChar(c)){
      if (l2rIdx < 0) l2rIdx = i;
    } else if (l2rIdx >= 0 && i-l2rIdx > 1){
      rev(l2rIdx, i);
      l2rIdx = -1;
    }
  }
  if (l2rIdx >= 0 && i-l2rIdx > 1) rev(l2rIdx, chars.length);
  return chars.join('');
}

// prepare_text_for_direction_rendering（text_render.py:181）
function prepareForDirection(text, horizontal){
  text = text || '';
  if (horizontal) return text.replace(H_BLOCK_MATCH_RE(), '$1');
  return text;
}

/* ================================================================
 * 文本测量（替代 QTextLayout / QRawFont 字形光栅）
 * ================================================================ */
let FONT_FAMILY = '"Arial Unicode MS","Microsoft YaHei","微软雅黑",sans-serif';
let FONT_FAMILY_NAME = 'Arial Unicode MS / 微软雅黑';
const probe = document.createElement('canvas').getContext('2d');

function fontStr(fs, bold, italic){
  return `${italic?'italic ':''}${bold?'bold ':''}${Math.round(fs)}px ${FONT_FAMILY}`;
}
// 单行测量：返回行栈与墨迹（fill ink）指标
function measureLine(line, fs, bold, italic, letterSpacing){
  probe.font = fontStr(fs, bold, italic);
  probe.textAlign = 'left';
  probe.textBaseline = 'alphabetic';
  try { probe.letterSpacing = letterSpacing !== 1 ? ((letterSpacing-1)*fs)+'px' : '0px'; } catch(e){}
  const m = probe.measureText(line);
  const fsAsc = m.fontBoundingBoxAscent  || fs*0.8;
  const fsDsc = m.fontBoundingBoxDescent || fs*0.2;
  const inkAsc = m.actualBoundingBoxAscent  ?? fs*0.8;
  const inkDsc = m.actualBoundingBoxDescent ?? 0;
  const inkLeft  = -(m.actualBoundingBoxLeft  ?? 0);   // anchor=0 时墨迹左缘
  const inkRight = (m.actualBoundingBoxRight ?? m.width); // 墨迹右缘
  return {
    text: line,
    ascent: fsAsc, descent: fsDsc, height: fsAsc + fsDsc,
    inkAsc: inkAsc, inkDsc: inkDsc,
    f0: inkLeft, f1: inkRight,
    inkW: Math.max(0, inkRight - inkLeft),
    inkH: inkAsc + inkDsc,
    hasInk: (inkRight-inkLeft) > 0.01 || inkAsc > 0.01,
    logical: m.width
  };
}
function measureChar(ch, fs, bold, italic, letterSpacing){
  return measureLine(ch, fs, bold, italic, letterSpacing);
}

/* ================================================================
 * 描边参数 — _resolve_stroke_ratio / bg_size / stroke_px
 *   stroke_ratio = region.stroke_width ?? 0.07
 *   bg_size  = int(max(fs*ratio, 1))
 *   stroke_px = max(int(fs*ratio), 1)   （两者数值恒等，见 text_render.py:683/912）
 * ================================================================ */
function strokeParams(r){
  const fs = Math.max(1, Math.round(r.font_size || 24));
  const ratio = (r.stroke_width ?? r.default_stroke_width ?? 0.07);
  const noBorder = !!r.disable_font_border;
  const strokeCol = r.bg_colors || null;   // render_params.text_stroke_color = params.bg_colors
  const stroke = (!noBorder && strokeCol) ? Math.max(pInt(fs*ratio), 1) : 0;
  return {fs, ratio, strokePx: stroke, strokeCol, fg: r.fg_colors || [0,0,0]};
}

/* ================================================================
 * 新建画布
 * ================================================================ */
function mkCanvas(w, h){
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.ceil(w));
  c.height = Math.max(1, Math.ceil(h));
  return c;
}
// 按 alpha>0 求墨迹包围盒 —— 对应 cv2.boundingRect(cv2.findNonZero(...))
function alphaBBox(ctx, w, h){
  const d = ctx.getImageData(0,0,w,h).data;
  let x0=w, y0=h, x1=-1, y1=-1;
  for (let y=0;y<h;y++){
    const row = y*w*4;
    for (let x=0;x<w;x++){
      if (d[row+x*4+3] !== 0){
        if (x<x0) x0=x; if (x>x1) x1=x;
        if (y<y0) y0=y; if (y>y1) y1=y;
      }
    }
  }
  if (x1<0) return null;
  return {x:x0, y:y0, w:x1-x0+1, h:y1-y0+1};
}
function cropToInk(canvas){
  const ctx = canvas.getContext('2d');
  const bb = alphaBBox(ctx, canvas.width, canvas.height);
  if (!bb) return null;
  if (bb.w===canvas.width && bb.h===canvas.height && bb.x===0 && bb.y===0) return canvas;
  const out = mkCanvas(bb.w, bb.h);
  out.getContext('2d').drawImage(canvas, -bb.x, -bb.y);
  return out;
}

/* ================================================================
 * 三 pass 上色（替代 add_color + 距离变换描边，text_render.py:208）
 *   ① 全部行描边色填充 ② 全部行描边 ③ 全部行前景填充
 *   数学上等价于原版 rgb = fg*a_t + bg*(1-a_t), alpha = max(a_t,a_s)
 * ================================================================ */
function drawStrokeFill(ctx, text, x, y, sp, sp2, fs, bold, italic, letterSpacing){
  ctx.font = fontStr(fs, bold, italic);
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  try { ctx.letterSpacing = letterSpacing !== 1 ? ((letterSpacing-1)*fs)+'px' : '0px'; } catch(e){}
  if (sp2.strokePx > 0){
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.lineWidth = sp2.strokePx * 2;   // QPainterPathStroker width = stroke_px*2（外扩 stroke_px）
    ctx.fillStyle = rgbStr(sp2.strokeCol);
    ctx.strokeStyle = rgbStr(sp2.strokeCol);
    ctx.fillText(text, x, y);
    ctx.strokeText(text, x, y);
  }
}
function fillFg(ctx, text, x, y, sp2, fs, bold, italic, letterSpacing){
  ctx.font = fontStr(fs, bold, italic);
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  try { ctx.letterSpacing = letterSpacing !== 1 ? ((letterSpacing-1)*fs)+'px' : '0px'; } catch(e){}
  ctx.fillStyle = rgbStr(sp2.fg);
  ctx.fillText(text, x, y);
}
/* ================================================================
 * 横排渲染 — put_text_horizontal（text_render.py:1195）逐行移植
 *   · _BR_RE 换行（不做自动折行：原版忽略 width/height）
 *   · 行栈 tops[i]、行距 spacing_y = int(fs*(ls-1))
 *   · 每行按 alignment 落在 [0, maxVisualWidth] 槽内
 *   · 行内墨迹（含描边）左缘对齐 target_left
 * ================================================================ */
function renderHorizontal(r, sp, text){
  const fs = sp.fs;
  const ls = r.line_spacing ?? 1.0;
  const lsp = r.letter_spacing ?? 1.0;
  const bold = !!r.bold, italic = !!r.italic;
  const s = sp.strokePx;
  const align = alignmentOf(r);

  const lines = text.replace(brRe(), '\n').split('\n');
  const spacingY = pInt(fs * ((ls>0?ls:1.0) - 1.0));      // calc_horizontal_line_spacing_px

  // 行栈
  const tops = []; let logicalY = 0;
  const ms = lines.map((ln, i) => {
    const m = measureLine(ln, fs, bold, italic, lsp);
    tops.push(logicalY);
    logicalY += m.height + (i < lines.length-1 ? spacingY : 0);
    return m;
  });

  // 行视觉宽（含描边）与槽宽
  const lw = ms.map(m => (m.hasInk ? m.inkW : 0) + 2*s);
  const maxVisual = Math.max(0, ...lw);

  const M = fs + s + 12;                                  // 画布边距（仅保证不裁切）
  const W = Math.ceil(maxVisual + 2*M + fs);
  const H = Math.ceil(logicalY + 2*M + fs);
  const canvas = mkCanvas(W, H);
  const ctx = canvas.getContext('2d');

  const target = lw.map(w =>
    align==='left'   ? 0
    : align==='center' ? Math.round((maxVisual - w)/2)
    :                   Math.round(maxVisual - w)          // right
  );

  // pass ①②：所有行 描边底色 + 描边
  ms.forEach((m, i) => {
    if (!m.hasInk || !m.text) return;
    const anchor = M + target[i] + s - m.f0;
    const baseline = M + tops[i] + m.ascent;
    drawStrokeFill(ctx, m.text, anchor, baseline, sp, sp, fs, bold, italic, lsp);
  });
  // pass ③：所有行 前景填充（保证相邻行交叠时 fg 不被描边覆盖）
  ms.forEach((m, i) => {
    if (!m.hasInk || !m.text) return;
    const anchor = M + target[i] + s - m.f0;
    const baseline = M + tops[i] + m.ascent;
    fillFg(ctx, m.text, anchor, baseline, sp, fs, bold, italic, lsp);
  });

  return cropToInk(canvas);
}

/* ================================================================
 * 竖排字形槽位 — _vertical_base（text_render.py:1014）
 * 返回 advance_y / 墨迹尺寸；含省略号 gap 算法与半宽括号
 * ================================================================ */
// 连通域标注（替代 cv2.connectedComponentsWithStats）— 字形画布很小，直接洪水填充
function componentYCenters(alpha, w, h){
  const seen = new Uint8Array(w*h);
  const centers = [];
  const stack = [];
  for (let i=0;i<w*h;i++){
    if (alpha[i]===0 || seen[i]) continue;
    // 新连通域
    stack.length = 0; stack.push(i); seen[i]=1;
    let sumY=0, cnt=0;
    while (stack.length){
      const p = stack.pop();
      const py = (p/w)|0, px = p%w;
      sumY += py; cnt++;
      const neigh = [];
      if (px>0)   neigh.push(p-1);
      if (px<w-1) neigh.push(p+1);
      if (py>0)   neigh.push(p-w);
      if (py<h-1) neigh.push(p+w);
      if (px>0&&py>0)   neigh.push(p-w-1);
      if (px<w-1&&py>0) neigh.push(p-w+1);
      if (px>0&&py<h-1) neigh.push(p+w-1);
      if (px<w-1&&py<h-1) neigh.push(p+w+1);
      for (const q of neigh){ if (!seen[q] && alpha[q]!==0){ seen[q]=1; stack.push(q); } }
    }
    if (cnt>0) centers.push(sumY/cnt);
  }
  return centers.sort((a,b)=>a-b);
}
// 把单字画到临时画布，返回填充墨迹度量（fill-only）
function rasterChar(ch, fs, bold, italic, lsp){
  const pad = Math.max(2, Math.ceil(fs*0.4));
  const c = mkCanvas(fs*3 + pad*2, fs*3 + pad*2);
  const ctx = c.getContext('2d');
  const baseX = pad + fs*0.6, baseY = pad + fs*1.1;
  ctx.font = fontStr(fs, bold, italic);
  ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
  try { ctx.letterSpacing = lsp!==1 ? ((lsp-1)*fs)+'px' : '0px'; } catch(e){}
  ctx.fillStyle = '#fff';
  ctx.fillText(ch, baseX, baseY);
  const bb = alphaBBox(ctx, c.width, c.height);
  if (!bb) return {canvas:c, inkW:0, inkH:0, x:0, y:0, alpha:null, aw:c.width, ah:c.height};
  const d = ctx.getImageData(0,0,c.width,c.height).data;
  const alpha = new Uint8Array(c.width*c.height);
  for (let i=0;i<c.width*c.height;i++) alpha[i] = d[i*4+3];
  // inkAsc = baseline 到墨迹顶的距离（墨迹顶 = bb.y）
  return {canvas:c, inkW:bb.w, inkH:bb.h, x:bb.x, y:bb.y, inkAsc: baseY - bb.y,
          alpha, aw:c.width, ah:c.height, baseX, baseY};
}
// _vertical_ellipsis_advance（text_render.py:1007）：3 连通域 → round(3*gap)
function ellipsisAdvance(raster, fs, lsp){
  let gap = 0;
  if (raster.alpha){
    const ys = componentYCenters(raster.alpha, raster.aw, raster.ah);
    if (ys.length >= 3) gap = ((ys[1]-ys[0]) + (ys[2]-ys[1])) / 2.0;
  }
  let adv = (gap > 0) ? Math.round(3*gap) : Math.max(1, raster.inkH);
  adv = Math.max(1, adv);
  return Math.max(1, Math.round(adv * (lsp>0?lsp:1.0)));   // _scale_advance
}
// 单个竖排字符槽位
const vBaseCache = new Map();
function verticalBase(ch, fs, lsp, bold, italic){
  const key = fs+'|'+ch+'|'+lsp+'|'+bold+'|'+italic;
  let b = vBaseCache.get(key);
  if (b) return b;
  // CJK_Compatibility_Forms_translate(cdpt, 1)：仅 'ー' 旋转 90°
  const rot90 = (ch === 'ー');
  const raster = rasterChar(ch, fs, bold, italic, lsp);
  let adv;
  if (V_ELLIPSIS.has(ch))      adv = ellipsisAdvance(raster, fs, lsp);
  else if (V_HALF.has(ch))     adv = Math.max(1, Math.round(fs*0.5 * (lsp>0?lsp:1.0)));
  else                         adv = Math.max(1, Math.round(Math.max(raster.inkH, fs) * (lsp>0?lsp:1.0)));
  b = {ch, rot90, raster, adv};
  vBaseCache.set(key, b);
  return b;
}

/* ================================================================
 * 竖排块渲染 — _block_surface（text_render.py:955）
 *   横排 <H>…</H> 内容；纯拉丁/数字词旋转 90°（should_rotate_horizontal_block_90）
 * ================================================================ */
function shouldRotateBlock90(content){
  const c = normalizeHBlockContent(content).trim();
  if (!c) return false;
  // Python: [a-zA-Z0-9\uff21-\uff3a\uff41-\uff5a\uff10-\uff19_-]+(?:[ \t]+[..]+)*
  return /^[a-zA-Z0-9Ａ-Ｚａ-ｚ０-９_-]+(?:[ \t]+[a-zA-Z0-9Ａ-Ｚａ-ｚ０-９_-]+)*$/u.test(c);
}
function normalizeHBlockContent(content){
  let c = (content||'').replace(brRe(), '').replace(/\r/g,'').replace(/\n/g,'');
  if (/^[!?！？]+$/.test(c)) c = c.replace(/！/g,'!').replace(/？/g,'?');
  return c;
}
function renderHBlock(content, sp, ls, lsp, bold, italic){
  const fs = sp.fs;
  const body = normalizeHBlockContent(content);
  if (!body) return null;
  const m = measureLine(body, fs, bold, italic, lsp);
  if (!m.hasInk) return null;
  const s = sp.strokePx;
  const W = Math.ceil(m.inkW + 2*s + fs + 8);
  const H = Math.ceil(m.inkH + 2*s + fs + 8);
  const c = mkCanvas(W, H);
  const ctx = c.getContext('2d');
  const anchor = fs/2 + 4 + s - m.f0;
  const baseline = fs/2 + 4 + s + m.inkAsc;
  drawStrokeFill(ctx, body, anchor, baseline, sp, sp, fs, bold, italic, lsp);
  fillFg(ctx, body, anchor, baseline, sp, fs, bold, italic, lsp);
  let surf = cropToInk(c);
  if (!surf) return null;
  if (shouldRotateBlock90(content)){
    // cv2.ROTATE_90_CLOCKWISE
    const rot = mkCanvas(surf.height, surf.width);
    const rctx = rot.getContext('2d');
    rctx.translate(rot.width, 0);
    rctx.rotate(Math.PI/2);
    rctx.drawImage(surf, 0, 0);
    surf = cropToInk(rot) || rot;
  }
  return surf;
}

/* ================================================================
 * 竖排布局 — _build_vertical_layout（text_render.py:1118）
 * 两遍：先算列宽 colW，再落每个槽位
 * ================================================================ */
function buildVerticalLayout(lineText, sp, lsp, bold, italic){
  const fs = sp.fs;
  let colW = fs;
  const parts = lineText.split(H_BLOCK_SPLIT_RE());   // 保留 <H>…</H> 段
  const raw = [];
  for (const part of parts){
    if (!part) continue;
    if (/^<H>.*<\/H>$/is.test(part)){
      const surf = renderHBlock(part.slice(3, -4), sp, 1, lsp, bold, italic);
      if (surf){ colW = Math.max(colW, surf.width); raw.push({kind:'block', surf}); }
      continue;
    }
    for (const ch of part){
      if (ch === '＿'){                                   // placeholder
        raw.push({kind:'ph', adv: Math.max(1, Math.round(fs*(lsp>0?lsp:1.0)))});
        continue;
      }
      const base = verticalBase(ch, fs, lsp, bold, italic);
      colW = Math.max(colW, Math.max(fs, Math.round(base.raster.inkW),
                       Math.ceil(base.raster.inkW), fs));   // frame_width = max(fs, advance_x, ink_w)
      colW = Math.max(colW, fs);
      raw.push({kind:'char', base});
    }
  }
  // 第二遍：cursor / x / y
  let cursor = 0;
  const items = [];
  const pad = Math.max(1, Math.round(fs*0.05));
  for (const it of raw){
    if (it.kind === 'block'){
      items.push({...it, cursorY: cursor, x: Math.round((colW - it.surf.width)/2), y: 0});
      cursor += it.surf.height;
    } else if (it.kind === 'ph'){
      items.push({...it, cursorY: cursor});
      cursor += it.adv;
    } else {
      const b = it.base;
      const inkW = b.raster.inkW, inkH = b.raster.inkH;
      // x：墨迹在列内水平定位（_build_vertical_layout:1170-1176）
      let x = Math.round((colW - inkW)/2);
      if (V_TOP_RIGHT.has(b.ch))        x = colW - inkW - pad;
      else if (V_BOTTOM_LEFT.has(b.ch)) x = pad;
      // y：槽内垂直定位（_vertical_base:1041-1047，ink_y 已并入公式）
      let y;
      if (V_TOP_RIGHT.has(b.ch) || V_TOP_CENTER.has(b.ch))       y = pad;
      else if (V_BOTTOM_LEFT.has(b.ch) || V_BOTTOM_CENTER.has(b.ch)) y = b.adv - inkH - pad;
      else                                                       y = Math.round((b.adv - inkH)/2);
      items.push({...it, cursorY: cursor, x, y});
      cursor += b.adv;
    }
  }
  return {width: Math.round(colW), height: Math.max(0, Math.round(cursor)), items};
}

/* ================================================================
 * 竖排渲染 — put_text_vertical（text_render.py:1267）
 *   · _convert_br_outside_h_tags 换行 → 列（右→左）
 *   · 列距 spacing_x = ls>=1 ? int(fs*0.2*ls) : int(fs*(ls-0.8))
 *   · alignment 作用于列内垂直起始（left=top / center / right=bottom）
 * ================================================================ */
function convertBrOutsideH(text){
  const parts = (text||'').split(H_BLOCK_SPLIT_RE());
  const out = [];
  for (const part of parts){
    if (!part) continue;
    if (/^<H>.*<\/H>$/is.test(part)){
      const chunks = part.slice(3,-4).split(brRe()).filter(c=>c);
      const norm = chunks.map(normalizeHBlockContent).filter(c=>c).map(c=>`<H>${c}</H>`);
      out.push(norm.join('\n') || part);
    } else {
      out.push(part.replace(brRe(), '\n'));
    }
  }
  return out.join('');
}
function renderVertical(r, sp, text){
  const fs = sp.fs;
  const ls = r.line_spacing ?? 1.0;
  const lsp = r.letter_spacing ?? 1.0;
  const bold = !!r.bold, italic = !!r.italic;
  const s = sp.strokePx;
  const align = alignmentOf(r);
  const valLs = (typeof ls==='number' && ls>0) ? ls : 1.0;
  const spacingX = valLs >= 1.0 ? pInt(fs*0.2*valLs) : pInt(fs*(valLs-0.8));

  const lines = convertBrOutsideH(text).split('\n');
  const layouts = lines.map(ln => buildVerticalLayout(ln, sp, lsp, bold, italic));
  const colW = layouts.map(l => l.width);
  const maxH = Math.max(0, ...layouts.map(l => l.height));
  const contentW = colW.reduce((a,b)=>a+b,0) + spacingX*Math.max(0, layouts.length-1);

  const M = fs + s + 12;
  const W = Math.ceil(contentW + 2*M);
  const H = Math.ceil(maxH + 2*M);
  const canvas = mkCanvas(W, H);
  const ctx = canvas.getContext('2d');

  // 列右→左（current_edge 从 contentW 右缘开始）
  let edge = contentW;
  const starts = [];
  for (const w of colW){ starts.push(Math.round(edge - w)); edge -= w + spacingX; }

  const origins = layouts.map(l => {
    let o = 0;
    if (align==='center') o = Math.round((maxH - l.height)/2);
    else if (align==='right') o = maxH - l.height;     // 'right' = 列尾（下端）对齐
    return o;
  });

  // pass ①：块（整块贴）+ 字符描边底/描边
  const drawList = [];
  layouts.forEach((lay, li) => {
    const lineStart = M + starts[li];
    const originY = M + origins[li];
    for (const it of lay.items){
      if (it.kind === 'block'){
        drawList.push({op:'block', it, lineStart, originY});
      } else if (it.kind === 'char' && it.base.raster.inkW>0){
        drawList.push({op:'char', it, lineStart, originY});
      }
    }
  });
  for (const d of drawList){
    if (d.op === 'block'){
      ctx.drawImage(d.it.surf, d.lineStart + d.it.x, d.originY + d.it.cursorY);
    } else {
      drawVCharStroke(ctx, d, sp, fs, bold, italic, lsp);
    }
  }
  // pass ②：字前景
  for (const d of drawList){
    if (d.op === 'char') drawVCharFg(ctx, d, sp, fs, bold, italic, lsp);
  }
  return cropToInk(canvas);
}
// 竖排字符绘制：墨迹左上角对齐 (targetX, targetY)（原版 bitmap paste 语义）
function vCharAnchor(d, b){
  // 原版：draw_x = line_start + x，bitmap ink 偏移 ink_x/ink_y
  // 等价：fill 墨迹左上角落在 (lineStart + x, originY + cursorY + y)
  const targetX = d.lineStart + d.it.x;
  const targetY = d.originY + d.it.cursorY + d.it.y;
  // 以 raster 画布为中介：fill 墨迹左上 = (targetX, targetY)
  // anchor 处墨迹左 = ax + (raster.x - baseX)、墨迹顶 = ay - inkAsc
  return {targetX, targetY, ax: targetX - b.raster.x + b.raster.baseX, ay: targetY + b.raster.inkAsc};
}
function drawVCharStroke(ctx, d, sp, fs, bold, italic, lsp){
  const b = d.it.base;
  if (b.raster.inkW<=0 || sp.strokePx<=0 || !sp.strokeCol) return;
  const {targetX, targetY, ax, ay} = vCharAnchor(d, b);
  const fs2 = fontStr(fs, bold, italic);
  if (b.rot90){
    // 'ー'：字形顺时针旋转 90° 后贴（_vertical_base rot=90）
    const t = mkCanvas(b.raster.inkW + 2*sp.strokePx + 8, b.raster.inkH + 2*sp.strokePx + 8);
    const tc = t.getContext('2d');
    const cax = 4 + sp.strokePx, cay = 4 + sp.strokePx + b.raster.inkAsc;
    tc.font = fs2; tc.textAlign='left'; tc.textBaseline='alphabetic';
    tc.lineJoin='round'; tc.lineCap='round'; tc.lineWidth = sp.strokePx*2;
    tc.fillStyle = rgbStr(sp.strokeCol); tc.strokeStyle = rgbStr(sp.strokeCol);
    tc.fillText(b.ch, cax, cay); tc.strokeText(b.ch, cax, cay);
    const rot = mkCanvas(t.height, t.width);
    const rc = rot.getContext('2d');
    rc.translate(rot.width, 0); rc.rotate(Math.PI/2); rc.drawImage(t, 0, 0);
    const bb = cropToInk(rot);
    if (bb) ctx.drawImage(bb, targetX - sp.strokePx, targetY - sp.strokePx);
  } else {
    ctx.font = fs2; ctx.textAlign='left'; ctx.textBaseline='alphabetic';
    try { ctx.letterSpacing = lsp!==1 ? ((lsp-1)*fs)+'px' : '0px'; } catch(e){}
    ctx.lineJoin='round'; ctx.lineCap='round'; ctx.lineWidth = sp.strokePx*2;
    ctx.fillStyle = rgbStr(sp.strokeCol); ctx.strokeStyle = rgbStr(sp.strokeCol);
    ctx.fillText(b.ch, ax, ay); ctx.strokeText(b.ch, ax, ay);
  }
}
function drawVCharFg(ctx, d, sp, fs, bold, italic, lsp){
  const b = d.it.base;
  if (b.raster.inkW<=0) return;
  const {targetX, targetY, ax, ay} = vCharAnchor(d, b);
  const fs2 = fontStr(fs, bold, italic);
  if (b.rot90){
    const t = mkCanvas(b.raster.inkW + 8, b.raster.inkH + 8);
    const tc = t.getContext('2d');
    tc.font = fs2; tc.textAlign='left'; tc.textBaseline='alphabetic';
    tc.fillStyle = rgbStr(sp.fg);
    tc.fillText(b.ch, 4, 4 + b.raster.inkAsc);
    const rot = mkCanvas(t.height, t.width);
    const rc = rot.getContext('2d');
    rc.translate(rot.width, 0); rc.rotate(Math.PI/2); rc.drawImage(t, 0, 0);
    const bb = cropToInk(rot);
    if (bb) ctx.drawImage(bb, targetX, targetY);
  } else {
    ctx.font = fs2; ctx.textAlign='left'; ctx.textBaseline='alphabetic';
    try { ctx.letterSpacing = lsp!==1 ? ((lsp-1)*fs)+'px' : '0px'; } catch(e){}
    ctx.fillStyle = rgbStr(sp.fg);
    ctx.fillText(b.ch, ax, ay);
  }
}
/* ================================================================
 * 单区域渲染 — text_renderer_backend.render_text_image_for_region 的编辑器路径
 * ================================================================ */
function renderRegionSurface(r){
  const sp = strokeParams(r);
  const horiz = isHorizontal(r);
  let text = translationForRendering(r);
  text = prepareForDirection(text, horiz);
  if (!text || !text.trim()) return null;               // 空译文 → 跳过（原版 EDITOR RENDER SKIPPED）
  return horiz ? renderHorizontal(r, sp, text) : renderVertical(r, sp, text);
}

/* dst 盒 — render_box_rect_local + center（region_render_snapshot.py:18 / region_geometry_state.py:124）
 * 编辑器加载时优先用 JSON 里存好的渲染盒，不重跑 calc_box_from_font；
 * 区域被编辑过（文本/字号变化）则按当前渲染面围绕 center 重算 —— 对应
 * 原版 calculate_region_dst_points 在文字/字号变化后重新 calc_box_from_font */
const dirty = new Set();            // 被用户编辑过的区域索引
let ORIG_BACKUP = [];               // 原始 translation / font_size 备份
function boxFromSurface(i, r){
  const s = surfCache[i];
  if (!s) return dstBoxOrig(r);
  const c = r.center || [0,0];
  const w = s.width, h = s.height;
  const x0 = Math.round(c[0] - w/2), y0 = Math.round(c[1] - h/2);
  return {x:x0, y:y0, w:Math.round(w), h:Math.round(h), ew:w, eh:h};
}
function dstBox(r, i){
  if (i !== undefined && dirty.has(i)) return boxFromSurface(i, r);
  return dstBoxOrig(r);
}
function dstBoxOrig(r){
  const c = r.center || [0,0];
  const rb = r.render_box_rect_local;
  if (rb && rb.length === 4){
    const x0 = Math.round(c[0] + rb[0]), y0 = Math.round(c[1] + rb[1]);
    const x1 = Math.round(c[0] + rb[2]), y1 = Math.round(c[1] + rb[3]);
    // cv2.boundingRect(点集) 为闭区间：w = maxx-minx+1
    return {x:x0, y:y0, w:x1-x0+1, h:y1-y0+1, ew:rb[2]-rb[0], eh:rb[3]-rb[1]};
  }
  // 回退：lines 的 min_rect（空译文/缺字段时）
  const pts = (r.lines||[]).flat();
  if (!pts.length) return null;
  const xs = pts.map(p=>p[0]), ys = pts.map(p=>p[1]);
  const x0 = Math.round(Math.min(...xs)), y0 = Math.round(Math.min(...ys));
  const x1 = Math.round(Math.max(...xs)), y1 = Math.round(Math.max(...ys));
  return {x:x0, y:y0, w:x1-x0+1, h:y1-y0+1, ew:x1-x0, eh:y1-y0};
}

/* 把渲染面按原版方式贴到目标盒（宽高比补边 + 拉伸） */
function blitSurface(ctx, surf, box){
  const rOrig = box.ew / box.eh;                         // norm_h / norm_v（未取整）
  let cw = surf.width, ch = surf.height, ox = 0, oy = 0;
  const rTemp = cw / ch;
  if (rTemp > rOrig){
    const hExt = Math.floor((cw / rOrig - ch) / 2);      // int(... // 2)
    if (hExt >= 0){ ch += hExt*2; oy = hExt; }
  } else {
    const wExt = Math.floor((ch * rOrig - cw) / 2);
    if (wExt >= 0){ cw += wExt*2; ox = wExt; }
  }
  const padded = (ox||oy) ? mkCanvas(cw, ch) : surf;
  if (ox||oy) padded.getContext('2d').drawImage(surf, ox, oy);
  ctx.drawImage(padded, box.x, box.y, box.w, box.h);     // _resize_to_target_rect → (w_s,h_s)
}

/* ================================================================
 * 页面合成（缓存）与覆盖层（框/hover/蒙版）
 * ================================================================ */
const baseCanvas = $('baseCanvas'), overCanvas = $('overCanvas');
baseCanvas.width = overCanvas.width = PAGE_W;
baseCanvas.height = overCanvas.height = PAGE_H;
const bctx = baseCanvas.getContext('2d');
const octx = overCanvas.getContext('2d');

const state = {base:'inpaint', text:true, sel:-1, hover:-1, zoom:1};
let surfCache = [];        // 每区域渲染面缓存
let baseImg = null, origImg = null;
let renderMs = 0;

function loadImage(src){
  return new Promise((res, rej) => {
    const im = new Image();
    im.crossOrigin = 'anonymous';
    im.onload = () => res(im);
    im.onerror = () => rej(new Error('图片加载失败: ' + src.slice(0,40)));
    im.src = src;
  });
}

function buildSurfaces(){
  const t0 = performance.now();
  vBaseCache.clear();
  surfCache = REGIONS.map(r => { try { return renderRegionSurface(r); } catch(e){ console.error(e); return null; } });
  renderMs = performance.now() - t0;
}

function buildComposite(){
  bctx.clearRect(0, 0, PAGE_W, PAGE_H);
  const img = state.base === 'orig' ? origImg : baseImg;
  if (img) bctx.drawImage(img, 0, 0, PAGE_W, PAGE_H);
  if (state.text){
    for (let i=0;i<REGIONS.length;i++){
      const s = surfCache[i]; if (!s) continue;
      const box = dstBox(REGIONS[i], i); if (!box) continue;
      blitSurface(bctx, s, box);
    }
  }
}

function regionLinesBox(r, i){
  const pts = (r.lines||[]).flat();
  if (!pts.length) { const b = dstBox(r, i); return b; }
  const xs = pts.map(p=>p[0]), ys = pts.map(p=>p[1]);
  return {x:Math.min(...xs), y:Math.min(...ys), w:Math.max(...xs)-Math.min(...xs), h:Math.max(...ys)-Math.min(...ys)};
}

function buildOverlay(){
  octx.clearRect(0, 0, PAGE_W, PAGE_H);
  // hover：检测框高亮；选中：渲染盒 + 8 个控制点（可拖拽）
  const hov = state.hover;
  if (hov >= 0 && hov < REGIONS.length && hov !== state.sel){
    const b = regionLinesBox(REGIONS[hov], hov);
    octx.lineWidth = Math.max(2, PAGE_W/700);
    octx.strokeStyle = '#e6b450';
    octx.strokeRect(b.x, b.y, b.w, b.h);
  }
  if (state.sel >= 0 && state.sel < REGIONS.length){
    const d = dstBox(REGIONS[state.sel], state.sel);
    if (d){
      const lw = Math.max(2, PAGE_W/700);
      octx.lineWidth = lw;
      octx.strokeStyle = '#4f8cff';
      octx.strokeRect(d.x, d.y, d.w, d.h);
      // 8 个手柄：四角 + 四边中点
      const hs = Math.max(7, PAGE_W/200);
      const pts = handlePoints(d);
      octx.fillStyle = '#4f8cff';
      octx.strokeStyle = '#fff';
      octx.lineWidth = Math.max(1, lw/2);
      for (const [hx, hy] of pts){
        octx.fillRect(hx-hs/2, hy-hs/2, hs, hs);
        octx.strokeRect(hx-hs/2, hy-hs/2, hs, hs);
      }
    }
  }
}
function handlePoints(b){
  const cx = b.x + b.w/2, cy = b.y + b.h/2;
  return [
    [b.x, b.y], [cx, b.y], [b.x+b.w, b.y],
    [b.x, cy],               [b.x+b.w, cy],
    [b.x, b.y+b.h], [cx, b.y+b.h], [b.x+b.w, b.y+b.h]
  ];
}

function renderAll(){
  buildSurfaces();
  buildComposite();
  buildOverlay();
  updateStatus();
}

/* ================================================================
 * 区域列表
 * ================================================================ */
function buildRegionList(){
  const el = $('regionList');
  el.innerHTML = '';
  ORIG_BACKUP = REGIONS.map(r => ({translation: r.translation, font_size: r.font_size,
                                   alignment: r.alignment, direction: r.direction}));
  REGIONS.forEach((r, i) => {
    const div = document.createElement('div');
    div.className = 'ritem';
    div.dataset.idx = i;
    const dir = isHorizontal(r) ? '横' : '竖';
    div.innerHTML = `<div class="row1"><span>#${i+1} · ${dir}排 · ${r.font_size}px</span><span>${r.target_lang||''}</span></div>`
      + `<div class="txt">${escapeHtml(r.translation||'')}</div>`
      + `<div class="txt src">${escapeHtml(r.text||'')}</div>`;
    div.addEventListener('click', () => { state.sel = i; syncSel(); });
    div.addEventListener('mouseenter', () => { if (state.hover !== i){ state.hover = i; buildOverlay(); } });
    div.addEventListener('mouseleave', () => { if (state.hover === i){ state.hover = -1; buildOverlay(); } });
    el.appendChild(div);
  });
  $('sideHead').textContent = `区域列表（${REGIONS.length}）`;
}
function escapeHtml(s){ return String(s).replace(/[&<>"]/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;'}[c])); }
function syncSel(){
  document.querySelectorAll('.ritem').forEach((el,i) => el.classList.toggle('sel', i===state.sel));
  showEditPanel();
  buildOverlay();
}

/* ================================================================
 * 编辑面板：译文修改 + 字号调整（防抖重建单区域）
 * ================================================================ */
let editTimer = null;
function rebuildRegion(i){
  const t0 = performance.now();
  try { surfCache[i] = renderRegionSurface(REGIONS[i]); }
  catch(e){ console.error(e); surfCache[i] = null; }
  renderMs = performance.now() - t0;
  buildComposite();
  buildOverlay();
  updateListItem(i);
  updateStatus();
}
function scheduleRebuild(i){
  clearTimeout(editTimer);
  editTimer = setTimeout(() => { dirty.add(i); rebuildRegion(i); }, 120);
}
function updateListItem(i){
  const el = document.querySelector(`.ritem[data-idx="${i}"] .txt`);
  if (el) el.textContent = REGIONS[i].translation || '';
}
function showEditPanel(){
  const panel = $('editPanel');
  if (state.sel < 0){ panel.classList.remove('show'); return; }
  const r = REGIONS[state.sel];
  $('epTitle').textContent = `编辑区域 #${state.sel+1}`;
  if (document.activeElement !== $('epText')) $('epText').value = r.translation || '';
  $('epSize').value = r.font_size;
  $('epSizeNum').value = r.font_size;
  $('epSizeVal').textContent = r.font_size;
  syncAlignBtns();
  panel.classList.add('show');
}
// 对齐按钮：横排=左中右，竖排=上中下（语义同原版 alignment）
function syncAlignBtns(){
  if (state.sel < 0) return;
  const r = REGIONS[state.sel];
  const a = alignmentOf(r);
  const vert = !isHorizontal(r);
  $('epAlignL').textContent = vert ? '上' : '左';
  $('epAlignR').textContent = vert ? '下' : '右';
  for (const btn of $('epAlign').querySelectorAll('button')){
    btn.classList.toggle('on', btn.dataset.a === a);
  }
  // 方向按钮高亮
  const dir = vert ? 'v' : 'h';
  for (const btn of $('epDir').querySelectorAll('button')){
    btn.classList.toggle('on', btn.dataset.d === dir);
  }
}
// 横排/竖排切换（改 region.direction，渲染盒随内容自动重算）
$('epDir').addEventListener('click', e => {
  const b = e.target.closest('button[data-d]');
  if (!b || state.sel < 0) return;
  const r = REGIONS[state.sel];
  const vert = !isHorizontal(r);
  if ((vert ? 'v' : 'h') === b.dataset.d) return;
  r.direction = b.dataset.d;
  syncAlignBtns();
  dirty.add(state.sel);
  rebuildRegion(state.sel);
});
$('epAlign').addEventListener('click', e => {
  const b = e.target.closest('button[data-a]');
  if (!b || state.sel < 0) return;
  const r = REGIONS[state.sel];
  r.alignment = b.dataset.a;
  syncAlignBtns();
  dirty.add(state.sel);
  rebuildRegion(state.sel);
});
function applyFontSize(v){
  if (state.sel < 0) return;
  if (v === '' || v === null || v === undefined) return;
  v = Math.round(Number(v));
  if (!isFinite(v)) return;
  v = clamp(v, 8, 160);
  const r = REGIONS[state.sel];
  if (r.font_size === v) return;
  r.font_size = v;
  $('epSize').value = v;
  $('epSizeNum').value = v;
  $('epSizeVal').textContent = v;
  scheduleRebuild(state.sel);
}
$('epText').addEventListener('input', () => {
  if (state.sel < 0) return;
  REGIONS[state.sel].translation = $('epText').value;
  scheduleRebuild(state.sel);
});
$('epSize').addEventListener('input', e => applyFontSize(e.target.value));
$('epSizeNum').addEventListener('input', e => applyFontSize(e.target.value));
$('epReset').addEventListener('click', () => {
  const i = state.sel;
  if (i < 0) return;
  clearTimeout(editTimer);
  const r = REGIONS[i], b = ORIG_BACKUP[i];
  r.translation = b.translation;
  r.font_size = b.font_size;
  r.alignment = b.alignment;
  r.direction = b.direction;
  dirty.delete(i);
  $('epText').value = r.translation || '';
  $('epSize').value = r.font_size;
  $('epSizeNum').value = r.font_size;
  $('epSizeVal').textContent = r.font_size;
  syncAlignBtns();
  rebuildRegion(i);
});

/* ================================================================
 * 缩放
 * ================================================================ */
function fitZoom(){
  const vp = $('viewport');
  return Math.min((vp.clientWidth-48)/PAGE_W, (vp.clientHeight-48)/PAGE_H);
}
function applyZoom(z, label){
  state.zoom = clamp(z, 0.05, 6);
  for (const c of [baseCanvas, overCanvas]){
    c.style.width = (PAGE_W*state.zoom)+'px';
    c.style.height = (PAGE_H*state.zoom)+'px';
  }
  $('zoomLabel').textContent = label || Math.round(state.zoom*100)+'%';
}

/* ================================================================
 * 状态栏
 * ================================================================ */
function updateStatus(){
  $('stState').innerHTML = `<span class="ok">渲染完成</span> · ${renderMs.toFixed(0)}ms · ${PAGE_W}×${PAGE_H}`;
  const nText = surfCache.filter(Boolean).length;
  $('stRegions').textContent = `区域 ${nText}/${REGIONS.length} 已渲染`;
  const ok = document.fonts.check(`16px ${FONT_FAMILY}`);
  $('stFont').innerHTML = ok ? '<span class="ok">字体可用</span>' : '<span class="warn">字体缺失（将回退系统字体）</span>';
}

/* ================================================================
 * 交互
 * ================================================================ */
function pageXY(ev){
  const rect = baseCanvas.getBoundingClientRect();
  return {
    x: (ev.clientX - rect.left) / state.zoom,
    y: (ev.clientY - rect.top) / state.zoom
  };
}
function hitRegion(p){
  for (let i=REGIONS.length-1;i>=0;i--){
    const b = regionLinesBox(REGIONS[i], i);
    if (b && p.x>=b.x-4 && p.x<=b.x+b.w+4 && p.y>=b.y-4 && p.y<=b.y+b.h+4) return i;
  }
  return -1;
}
function inBox(p, b){ return p.x>=b.x && p.x<=b.x+b.w && p.y>=b.y && p.y<=b.y+b.h; }
// 命中渲染盒控制点（页面坐标，屏幕容差约 10px）
function hitHandle(p, b){
  const tol = 10 / state.zoom;
  const cx = b.x + b.w/2, cy = b.y + b.h/2;
  const nz = (a, c) => Math.abs(a - c) <= tol;
  const corners = [['nw',b.x,b.y],['ne',b.x+b.w,b.y],['sw',b.x,b.y+b.h],['se',b.x+b.w,b.y+b.h]];
  for (const [m,x,y] of corners) if (nz(p.x,x) && nz(p.y,y)) return m;
  if (nz(p.y, b.y)       && p.x>=b.x-tol && p.x<=b.x+b.w+tol) return 'n';
  if (nz(p.y, b.y+b.h)   && p.x>=b.x-tol && p.x<=b.x+b.w+tol) return 's';
  if (nz(p.x, b.x)       && p.y>=b.y-tol && p.y<=b.y+b.h+tol) return 'w';
  if (nz(p.x, b.x+b.w)   && p.y>=b.y-tol && p.y<=b.y+b.h+tol) return 'e';
  return null;
}
const HANDLE_CURSOR = {nw:'nwse-resize', se:'nwse-resize', ne:'nesw-resize', sw:'nesw-resize',
                       n:'ns-resize', s:'ns-resize', w:'ew-resize', e:'ew-resize'};

/* 拖拽：框内移动（改 center）/ 角边缩放（按比例改字号，同原版 calc_font_from_box 语义） */
let drag = null, justDragged = false, rafDrag = 0, pendingDragP = null;
overCanvas.addEventListener('mousedown', ev => {
  if (ev.button !== 0) return;
  justDragged = false;
  const p = pageXY(ev);
  if (state.sel >= 0 && state.sel < REGIONS.length){
    const box = dstBox(REGIONS[state.sel], state.sel);
    if (box){
      const h = hitHandle(p, box);
      if (h){
        drag = {mode:h, p0:p, box0:{...box}, fs0:REGIONS[state.sel].font_size, moved:false};
        ev.preventDefault();
        return;
      }
      if (inBox(p, box)){
        drag = {mode:'move', p0:p, c0:[...(REGIONS[state.sel].center || [0,0])], moved:false};
        ev.preventDefault();
        return;
      }
    }
  }
});
function applyDrag(p){
  if (!drag || state.sel < 0) return;
  const r = REGIONS[state.sel];
  if (drag.mode === 'move'){
    r.center = [
      Math.round(drag.c0[0] + (p.x - drag.p0.x)),
      Math.round(drag.c0[1] + (p.y - drag.p0.y))
    ];
    rebuildRegion(state.sel);
  } else {
    const b = drag.box0;
    let w = b.w, h = b.h;
    if (drag.mode.includes('e')) w = p.x - b.x;
    if (drag.mode.includes('w')) w = (b.x + b.w) - p.x;
    if (drag.mode.includes('s')) h = p.y - b.y;
    if (drag.mode.includes('n')) h = (b.y + b.h) - p.y;
    const rw = Math.abs(w) / Math.max(1, b.w);
    const rh = Math.abs(h) / Math.max(1, b.h);
    const corner = drag.mode.length > 1;
    const ratio = corner ? Math.min(rw, rh) : (drag.mode === 'n' || drag.mode === 's') ? rh : rw;
    if (!isFinite(ratio) || ratio <= 0.02) return;
    const fs = clamp(Math.round(drag.fs0 * ratio), 8, 160);
    if (fs !== r.font_size){
      r.font_size = fs;
      $('epSize').value = fs;
      $('epSizeNum').value = fs;
      $('epSizeVal').textContent = fs;
      dirty.add(state.sel);
      rebuildRegion(state.sel);
    } else {
      buildOverlay();
    }
  }
}
window.addEventListener('mousemove', ev => {
  if (drag){
    drag.moved = true;
    pendingDragP = pageXY(ev);
    if (!rafDrag) rafDrag = requestAnimationFrame(() => { rafDrag = 0; applyDrag(pendingDragP); });
    return;
  }
  const p = pageXY(ev);
  const idx = hitRegion(p);
  if (idx !== state.hover){ state.hover = idx; buildOverlay(); }
  let cur = idx >= 0 ? 'pointer' : 'default';
  if (state.sel >= 0 && state.sel < REGIONS.length){
    const box = dstBox(REGIONS[state.sel], state.sel);
    if (box){
      const h = hitHandle(p, box);
      if (h) cur = HANDLE_CURSOR[h];
      else if (inBox(p, box)) cur = 'move';
    }
  }
  overCanvas.style.cursor = cur;
});
window.addEventListener('mouseup', () => {
  if (drag){
    if (drag.moved) justDragged = true;
    drag = null;
  }
});
overCanvas.addEventListener('mouseleave', () => { if (state.hover!==-1){ state.hover=-1; buildOverlay(); } });
overCanvas.addEventListener('click', ev => {
  if (justDragged){ justDragged = false; return; }   // 刚结束拖拽，不当作选择点击
  const idx = hitRegion(pageXY(ev));
  state.sel = idx; syncSel();
  if (idx>=0){
    const item = document.querySelector(`.ritem[data-idx="${idx}"]`);
    if (item) item.scrollIntoView({block:'nearest'});
  }
});
$('viewport').addEventListener('wheel', ev => {
  if (!ev.ctrlKey) return;
  ev.preventDefault();
  applyZoom(state.zoom * (ev.deltaY < 0 ? 1.1 : 1/1.1));
}, {passive:false});

$('zoomIn').onclick  = () => applyZoom(state.zoom*1.2);
$('zoomOut').onclick = () => applyZoom(state.zoom/1.2);
$('zoomFit').onclick = () => applyZoom(fitZoom());
$('zoom100').onclick = () => applyZoom(1);

$('baseSel').onchange = e => { state.base = e.target.value; buildComposite(); };
$('cbText').onchange  = e => { state.text = e.target.checked; buildComposite(); };

$('exportBtn').onclick = () => {
  try {
    const mime = (EXPORT_EXT === '.jpg' || EXPORT_EXT === '.jpeg') ? 'image/jpeg' : 'image/png';
    const url = baseCanvas.toDataURL(mime, 0.95);
    const a = document.createElement('a');
    a.href = url;
    a.download = EXPORT_NAME;
    a.click();
    toast('已导出 ' + EXPORT_EXT.replace('.','').toUpperCase());
  } catch (e) {
    console.error('导出失败', e);
    toast('导出失败: ' + e.message);
  }
};

// 字体：相对路径（http 下可用）→ 本地文件
async function tryProjectFont(){
  try {
    const res = await fetch('fonts/Arial-Unicode-Regular.ttf');
    if (!res.ok) return false;
    const buf = await res.arrayBuffer();
    const ff = new FontFace('ArialUnicodeProject', buf);
    await ff.load();
    document.fonts.add(ff);
    setFont('ArialUnicodeProject', 'Arial-Unicode-Regular.ttf（项目字体）');
    return true;
  } catch(e){ return false; }
}
function setFont(familyCss, name){
  FONT_FAMILY = familyCss.startsWith('"') || familyCss.startsWith("'") ? familyCss : `"${familyCss}"`;
  FONT_FAMILY_NAME = name;
  $('fontInfo').textContent = '字体: ' + name;
  renderAll();
}
$('fontBtn').onclick = () => $('fontFile').click();
$('fontFile').onchange = async ev => {
  const f = ev.target.files[0];
  if (!f) return;
  try {
    const buf = await f.arrayBuffer();
    const ff = new FontFace('UserLoadedFont', buf);
    await ff.load();
    document.fonts.add(ff);
    setFont('UserLoadedFont', f.name);
    toast('字体已加载: ' + f.name);
  } catch(e){ toast('字体加载失败: ' + e.message); }
};

function toast(msg){
  $('stState').innerHTML = `<span class="ok">${escapeHtml(msg)}</span>`;
  setTimeout(updateStatus, 2500);
}

/* ================================================================
 * 启动
 * ================================================================ */
return (async function init(){
  $('docName').textContent = ' · ' + PAGE_KEY.replace(/^.*[\\\/]/,'');
  $('fontInfo').textContent = '字体: ' + FONT_FAMILY_NAME;
  buildRegionList();
  // 图片加载期间显示 loading，加载完成后再隐藏
  const emptyEl = $('empty');
  emptyEl.classList.remove('error');
  emptyEl.querySelector('.empty-text').textContent = '正在加载图片…';
  emptyEl.style.display = 'flex';
  $('stState').innerHTML = '<span class="warn">图片加载中…</span>';
  try {
    [origImg, baseImg] = await Promise.all([
      loadImage(ORIG_SRC),
      loadImage(INPAINT_SRC)
    ]);
  } catch(e){
    emptyEl.classList.add('error');
    emptyEl.querySelector('.empty-text').textContent = '加载失败: ' + e.message;
    $('stState').innerHTML = '<span class="warn">图片加载失败</span>';
    return;
  }
  $('stState').innerHTML = '<span class="warn">渲染中…</span>';
  await tryProjectFont();          // 若以 http(s) 打开且旁边有 fonts/，用项目原字体
  await document.fonts.ready;
  renderAll();
  // URL hash 可指定缩放：#zoom=1（100%）/ #zoom=fit；#sel=N 选中区域
  const zm = (location.hash.match(/zoom=([\d.]+)/) || [])[1];
  applyZoom(zm ? parseFloat(zm) : fitZoom());
  const sm = (location.hash.match(/sel=(\d+)/) || [])[1];
  if (sm){ state.sel = clamp(parseInt(sm,10)-1, 0, REGIONS.length-1); syncSel();
    const item = document.querySelector(`.ritem[data-idx="${state.sel}"]`);
    if (item) item.scrollIntoView({block:'nearest'}); }
  $('empty').style.display = 'none';
  window.addEventListener('resize', () => { /* 保持当前缩放，不打断用户 */ });
})();
}
